import { useState } from 'react';
import { Inbox } from 'lucide-react';

import type { SugestaoForense } from '@/api/calendarioForense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useNotification } from '@/context/NotificationProvider';
import { useRecusarSugestao, useSugestoesForenses } from '@/hooks/useCalendarioForense';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { descreverOnde, descreverQuando, fmtData, rotuloTipo } from './calendarioForense';
import { RegraForenseDialog } from './RegraForenseDialog';

type Situacao = 'pendente' | 'promovida' | 'recusada';

function onde(s: SugestaoForense): string {
  if (s.abrangencia === 'municipal' && !s.municipio_nome) {
    return `Município IBGE ${s.municipio_ibge ?? '?'}${s.uf ? ` (${s.uf})` : ''}`;
  }
  return descreverOnde(s);
}

function RecusarDialog({ sugestao, onClose }: { sugestao: SugestaoForense; onClose: () => void }) {
  const { showSuccess, showError } = useNotification();
  const recusar = useRecusarSugestao();
  const [nota, setNota] = useState('');
  return (
    <Dialog open onOpenChange={(open) => !open && !recusar.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recusar a sugestão?</DialogTitle>
          <DialogDescription>
            “{sugestao.nome}” não entra no calendário oficial. Na conta de quem a lançou ela continua valendo — a
            decisão sobre o processo é dele.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="recusa-nota">Motivo (opcional)</Label>
          <Textarea id="recusa-nota" rows={2} maxLength={300} value={nota} onChange={(e) => setNota(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={recusar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={recusar.isPending}
            onClick={() =>
              recusar.mutate(
                { id: sugestao.id, nota: nota.trim() || null },
                {
                  onSuccess: () => {
                    showSuccess('Sugestão recusada.');
                    onClose();
                  },
                  onError: (err) => {
                    if (classifyError(err).status === 409) {
                      showError('Esta sugestão já foi resolvida.');
                      onClose();
                      return;
                    }
                    notifyMutationError(err, showError);
                  },
                },
              )
            }
          >
            {recusar.isPending && <Spinner className="mr-2 text-inherit" />}
            Recusar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A fila do que os advogados lançaram nas próprias contas (Bloco E): promover a oficial ou recusar. */
export function SugestoesForenses() {
  const [situacao, setSituacao] = useState<Situacao>('pendente');
  const { data, isLoading, error } = useSugestoesForenses(situacao);
  const [promovendo, setPromovendo] = useState<SugestaoForense | null>(null);
  const [recusando, setRecusando] = useState<SugestaoForense | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label htmlFor="sugestoes-situacao">Mostrar</Label>
        <Select value={situacao} onValueChange={(v) => setSituacao(v as Situacao)}>
          <SelectTrigger id="sugestoes-situacao" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pendente">Pendentes</SelectItem>
            <SelectItem value="promovida">Promovidas</SelectItem>
            <SelectItem value="recusada">Recusadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <Spinner size={32} label="Carregando sugestões" />}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}
      {data && data.length === 0 && (
        <EmptyState
          icon={<Inbox />}
          title="Nenhuma sugestão"
          description={situacao === 'pendente' ? 'Nada esperando conferência.' : 'Nenhuma sugestão nesta situação.'}
        />
      )}

      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((s) => (
            <li key={s.id}>
              <Card>
                <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <div className="font-medium">{s.nome}</div>
                    <div className="text-sm">
                      {descreverQuando(s)} · {onde(s)}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={s.tipo === 'feriado' ? 'secondary' : 'outline'}>{rotuloTipo(s.tipo)}</Badge>
                    </div>
                    <div className="break-words text-sm text-muted-foreground">Ato: {s.ato_normativo}</div>
                    <div className="text-xs text-muted-foreground">
                      Lançada por {s.advogado_nome ?? 'advogado removido'}
                      {s.advogado_email ? ` (${s.advogado_email})` : ''} em {fmtData(s.created_at)}
                    </div>
                    {s.sugestao_nota && <div className="text-xs text-muted-foreground">Motivo: {s.sugestao_nota}</div>}
                  </div>
                  {situacao === 'pendente' && (
                    <div className="flex shrink-0 gap-2">
                      <Button variant="outline" className="min-h-11" onClick={() => setRecusando(s)}>
                        Recusar
                      </Button>
                      <Button className="min-h-11" onClick={() => setPromovendo(s)}>
                        Promover
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {promovendo && (
        <RegraForenseDialog
          key={promovendo.id}
          regra={null}
          sugestao={promovendo}
          onClose={() => setPromovendo(null)}
        />
      )}
      {recusando && <RecusarDialog key={recusando.id} sugestao={recusando} onClose={() => setRecusando(null)} />}
    </div>
  );
}
